/**
 * LineItemTable - Table component for a closing costs section
 *
 * Displays a table of line items for a specific section with:
 * - Header row with section title
 * - Editable line item rows
 * - Add new item functionality
 * - Section subtotal
 */

import { Plus } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import type { LineItem, LineItemSection } from '../../../types';
import { formatLineNumber, LineItemRow } from './LineItemRow';

export interface LineItemTableProps {
  /** Section identifier */
  section: LineItemSection;
  /** Section title */
  title: string;
  /** Line items for this section */
  items: LineItem[];
  /** Transaction ID for new items */
  transactionId: string;
  /** Change handler for items */
  onChange: (items: LineItem[]) => void;
  /** Whether the table is disabled */
  disabled?: boolean;
}

/** Section templates with common line items */
const SECTION_TEMPLATES: Record<LineItemSection, { description: string; lineNumber: string }[]> = {
  A: [
    { description: '% of Loan Amount (Points)', lineNumber: 'A.01' },
    { description: 'Application Fee', lineNumber: 'A.02' },
    { description: 'Underwriting Fee', lineNumber: 'A.03' },
  ],
  B: [
    { description: 'Appraisal Fee', lineNumber: 'B.01' },
    { description: 'Credit Report Fee', lineNumber: 'B.02' },
    { description: 'Flood Determination Fee', lineNumber: 'B.03' },
    { description: 'Tax Monitoring Fee', lineNumber: 'B.04' },
    { description: 'Tax Status Research Fee', lineNumber: 'B.05' },
  ],
  C: [
    { description: "Title - Title Services and Lender's Title Insurance", lineNumber: 'C.01' },
    { description: 'Title - Settlement Agent Fee', lineNumber: 'C.02' },
    { description: "Title - Owner's Title Insurance (optional)", lineNumber: 'C.03' },
    { description: "Title - Lender's Title Insurance", lineNumber: 'C.04' },
    { description: 'Survey Fee', lineNumber: 'C.05' },
    { description: 'Pest Inspection Fee', lineNumber: 'C.06' },
  ],
  E: [
    { description: 'Recording Fees - Deed', lineNumber: 'E.01' },
    { description: 'Recording Fees - Mortgage', lineNumber: 'E.02' },
    { description: 'Transfer Taxes', lineNumber: 'E.03' },
  ],
  F: [
    { description: "Homeowner's Insurance Premium", lineNumber: 'F.01' },
    { description: 'Mortgage Insurance Premium', lineNumber: 'F.02' },
    { description: 'Prepaid Interest', lineNumber: 'F.03' },
    { description: 'Property Taxes', lineNumber: 'F.04' },
  ],
  G: [
    { description: "Homeowner's Insurance per month", lineNumber: 'G.01' },
    { description: 'Mortgage Insurance per month', lineNumber: 'G.02' },
    { description: 'Property Taxes per month', lineNumber: 'G.03' },
  ],
  H: [
    { description: 'Real Estate Commission - Listing Agent', lineNumber: 'H.01' },
    { description: 'Real Estate Commission - Selling Agent', lineNumber: 'H.02' },
    { description: 'HOA Capital Contribution', lineNumber: 'H.03' },
    { description: 'HOA Processing Fee', lineNumber: 'H.04' },
    { description: 'Home Warranty Fee', lineNumber: 'H.05' },
    { description: 'Home Inspection Fee', lineNumber: 'H.06' },
  ],
  L: [
    { description: 'Deposit/Earnest Money', lineNumber: 'L.01' },
    { description: 'Seller Concessions', lineNumber: 'L.02' },
    { description: 'Property Tax Proration', lineNumber: 'L.03' },
    { description: 'HOA Proration', lineNumber: 'L.04' },
  ],
};

/** Section header descriptions */
const SECTION_HEADERS: Record<LineItemSection, string> = {
  A: 'Origination Charges',
  B: 'Services Borrower Did Not Shop For',
  C: 'Services Borrower Did Shop For',
  E: 'Taxes and Other Government Fees',
  F: 'Prepaids',
  G: 'Initial Escrow Payment at Closing',
  H: 'Other',
  L: 'Credits and Adjustments',
};

/**
 * Calculate section subtotals
 */
function calculateSectionTotals(items: LineItem[]) {
  return items.reduce(
    (acc, item) => ({
      borrowerAtClosing: acc.borrowerAtClosing + (item.borrowerPaidAtClosing ?? 0),
      borrowerBeforeClosing: acc.borrowerBeforeClosing + (item.borrowerPaidBeforeClosing ?? 0),
      sellerAtClosing: acc.sellerAtClosing + (item.sellerPaidAtClosing ?? 0),
      sellerBeforeClosing: acc.sellerBeforeClosing + (item.sellerPaidBeforeClosing ?? 0),
      paidByOthers: acc.paidByOthers + (item.paidByOthers ?? 0),
      total:
        acc.total +
        (item.borrowerPaidAtClosing ?? 0) +
        (item.borrowerPaidBeforeClosing ?? 0) +
        (item.sellerPaidAtClosing ?? 0) +
        (item.sellerPaidBeforeClosing ?? 0) +
        (item.paidByOthers ?? 0),
    }),
    {
      borrowerAtClosing: 0,
      borrowerBeforeClosing: 0,
      sellerAtClosing: 0,
      sellerBeforeClosing: 0,
      paidByOthers: 0,
      total: 0,
    },
  );
}

/**
 * Create a new empty line item
 */
function createEmptyLineItem(
  section: LineItemSection,
  transactionId: string,
  index: number,
): LineItem {
  return {
    transactionId,
    section,
    lineNumber: index + 1,
    lineNumberString: formatLineNumber(section, index),
    description: '',
    borrowerPaidAtClosing: undefined,
    borrowerPaidBeforeClosing: undefined,
    sellerPaidAtClosing: undefined,
    sellerPaidBeforeClosing: undefined,
    paidByOthers: undefined,
    isUserEntered: true,
  };
}

/**
 * LineItemTable - Table for a section's line items
 */
export const LineItemTable: React.FC<LineItemTableProps> = ({
  section,
  // title is used for accessibility and passed to parent context
  title: _title,
  items,
  transactionId,
  onChange,
  disabled = false,
}) => {
  const handleItemChange = React.useCallback(
    (index: number, updatedItem: LineItem) => {
      const newItems = [...items];
      newItems[index] = updatedItem;
      onChange(newItems);
    },
    [items, onChange],
  );

  const handleDeleteItem = React.useCallback(
    (index: number) => {
      const newItems = items.filter((_, i) => i !== index);
      // Re-number remaining items
      const renumbered = newItems.map((item, i) => ({
        ...item,
        lineNumber: i + 1,
        lineNumberString: formatLineNumber(section, i),
      }));
      onChange(renumbered);
    },
    [items, onChange, section],
  );

  const handleAddItem = React.useCallback(() => {
    const newItem = createEmptyLineItem(section, transactionId, items.length);
    onChange([...items, newItem]);
  }, [items, onChange, section, transactionId]);

  const handleLoadTemplate = React.useCallback(() => {
    const templateItems = SECTION_TEMPLATES[section].map((template, index) => ({
      transactionId,
      section,
      lineNumber: index + 1,
      lineNumberString: template.lineNumber,
      description: template.description,
      borrowerPaidAtClosing: undefined,
      borrowerPaidBeforeClosing: undefined,
      sellerPaidAtClosing: undefined,
      sellerPaidBeforeClosing: undefined,
      paidByOthers: undefined,
      isUserEntered: false,
    }));
    onChange(templateItems);
  }, [section, transactionId, onChange]);

  const totals = calculateSectionTotals(items);

  // Create header item for display
  const headerItem: LineItem = {
    transactionId,
    section,
    description: SECTION_HEADERS[section],
  };

  return (
    <div className="space-y-2">
      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-2 py-2 text-left w-14">Line #</th>
              <th className="px-2 py-2 text-left min-w-[200px]">Description</th>
              <th className="px-1 py-2 text-center w-28">Borrower at Closing</th>
              <th className="px-1 py-2 text-center w-28">Borrower Before</th>
              <th className="px-1 py-2 text-center w-28">Seller at Closing</th>
              <th className="px-1 py-2 text-center w-28">Seller Before</th>
              <th className="px-1 py-2 text-center w-28">Others</th>
              <th className="px-2 py-2 text-right w-20">Total</th>
              <th className="px-1 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {/* Section Header Row */}
            <LineItemRow
              item={headerItem}
              index={-1}
              section={section}
              onChange={() => {}}
              onDelete={() => {}}
              canDelete={false}
              isHeader
            />

            {/* Line Items */}
            {items.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                  <p className="mb-2">No items in this section</p>
                  <div className="flex justify-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleLoadTemplate}
                      disabled={disabled}
                    >
                      Load Template
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddItem}
                      disabled={disabled}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Item
                    </Button>
                  </div>
                </td>
              </tr>
            ) : (
              items.map((item, index) => (
                <LineItemRow
                  key={item.id ?? `${section}-${index}`}
                  item={item}
                  index={index}
                  section={section}
                  onChange={(updated) => handleItemChange(index, updated)}
                  onDelete={() => handleDeleteItem(index)}
                  canDelete={items.length > 0}
                  disabled={disabled}
                />
              ))
            )}

            {/* Subtotal Row */}
            {items.length > 0 && (
              <tr className="bg-muted font-semibold border-t-2">
                <td className="px-2 py-2 text-sm" colSpan={2}>
                  Section {section} Subtotal
                </td>
                <td className="px-1 py-2 text-right text-sm">
                  ${totals.borrowerAtClosing.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </td>
                <td className="px-1 py-2 text-right text-sm">
                  $
                  {totals.borrowerBeforeClosing.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                  })}
                </td>
                <td className="px-1 py-2 text-right text-sm">
                  ${totals.sellerAtClosing.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </td>
                <td className="px-1 py-2 text-right text-sm">
                  $
                  {totals.sellerBeforeClosing.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </td>
                <td className="px-1 py-2 text-right text-sm">
                  ${totals.paidByOthers.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </td>
                <td className="px-2 py-2 text-right text-sm font-bold">
                  ${totals.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </td>
                <td className="px-1 py-2 w-10"></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Item Button */}
      {items.length > 0 && (
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddItem}
            disabled={disabled}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Item
          </Button>
        </div>
      )}
    </div>
  );
};

LineItemTable.displayName = 'LineItemTable';

export { SECTION_TEMPLATES, SECTION_HEADERS, calculateSectionTotals, createEmptyLineItem };
